import { Grid, GridRow, Input } from '@bp/ui-components';
import { Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  useCreateCurriculumVersionMutation,
  useCurriculumVersionsListQuery,
  useUpdateCurriculumVersionsMutation,
} from '../../../types/planung-graphql-client-defs';
import { showErrorToast, showSuccessCreateToast, showSuccessUpdateToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { createCurriculumVersionSchema } from './validation/schema';

type CurriculumVersionFormProps = {
  selectedUuid: string | null;
  onLoad: (loading: boolean) => void;
  onClose: () => void;
};

type CurriculumVersionFormType = {
  uuid: string;
  name: string;
  description: string | null;
};

export const CurriculumVersionForm = observer(({ selectedUuid, onLoad, onClose }: CurriculumVersionFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const context = useMemorizedCacheTag('CURRICULUM_VERSIONS');

  const [, createCurriculumVersion] = useCreateCurriculumVersionMutation();
  const [, updateCurriculumVersion] = useUpdateCurriculumVersionsMutation();

  const [{ data: curriculumVersionsData }] = useCurriculumVersionsListQuery({
    variables: {
      where: {
        organization: { uuid: pimAuthClaims.getOrganizationUuid() },
        uuid: selectedUuid,
      },
    },
    context,
  });

  const currentVersion = curriculumVersionsData?.curriculumVersions[0] ?? null;

  const initialValues: CurriculumVersionFormType = {
    uuid: currentVersion?.uuid ?? '',
    description: currentVersion?.description ?? '',
    name: currentVersion?.name ?? '',
  };

  const handleSubmit = async (
    values: CurriculumVersionFormType,
    formikHelpers: FormikHelpers<CurriculumVersionFormType>,
  ) => {
    onLoad(true);

    if (currentVersion) {
      const { error } = await updateCurriculumVersion(
        {
          update: {
            description: values.description,
            name: values.name,
          },
          where: {
            uuid: currentVersion.uuid,
          },
        },
        context,
      );

      if (error) {
        showErrorToast(error);
      } else {
        showSuccessUpdateToast([values.name]);
      }
    } else {
      const { error } = await createCurriculumVersion(
        {
          input: {
            name: values.name,
            description: values.description,
            organization: { connect: { where: { node: { uuid: pimAuthClaims.getOrganizationUuid() } } } },
            validGrades: [0],
          },
        },
        context,
      );

      if (error) {
        showErrorToast(error);
      } else {
        showSuccessCreateToast([values.name]);
      }
    }

    formikHelpers.resetForm();
    onLoad(false);
    onClose();
  };

  return (
    <Formik<CurriculumVersionFormType>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={createCurriculumVersionSchema}
    >
      {({ values, resetForm, errors, handleChange, isSubmitting, dirty }) => (
        <Form>
          <Grid useFormGap>
            <GridRow spacingBottom='s'>
              <Input
                name='name'
                value={values.name}
                onChange={handleChange}
                label={t('common.name')}
                error={errors.name}
              />
            </GridRow>
            <GridRow spacingTop='s'>
              <Input
                name='description'
                value={values.description}
                onChange={handleChange}
                label={t('common.description')}
                error={errors.description}
              />
            </GridRow>
          </Grid>

          <ModalBottomButtons
            closeButton={{
              text: t('common.cancel'),
              callback: () => {
                resetForm();
                onClose();
              },
            }}
            submitButton={{
              disabled: !dirty || Object.keys(errors).length > 0,
            }}
            isLoading={isSubmitting}
            errors={errors}
          />
        </Form>
      )}
    </Formik>
  );
});
